import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { setFirmZone } from '@/components/ui'
import { clearActivity, idleTooLong, markActive, noteIdleSignOut } from '@/lib/idle'
import { useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { api, ApiError, setAccessToken, setAuthLostHandler, type Me } from '@/api/client'

interface AuthState {
  me: Me | null
  ready: boolean
  login: (email: string, password: string) => Promise<void>
  requestCode: (email: string) => Promise<void>
  setPassword: (email: string, code: string, newPassword: string) => Promise<void>
  signOut: () => Promise<void>
  can: (action: string) => boolean
}

const Ctx = createContext<AuthState | null>(null)

/** Both fragment handoffs, read once at module load.
 *
 *  `token` is the emergency 15-minute access token from `admin.py login-link`.
 *  `s` is the single-use token carried in a notification — an approver tapping
 *  "Open it on the site" has to arrive already signed in, or the notification has
 *  bought them a password prompt on a phone keyboard.
 *
 *  Read here rather than inside the effect because StrictMode mounts effects twice in
 *  development: the first pass erased the fragment, so the second found nothing and
 *  the handoff fell through to the sign-in screen — the one situation it exists for.
 *
 *  Both live in the fragment for the same reason: it never leaves the browser, so the
 *  token stays out of this server's access log, Caddy's, and anything in between — a
 *  query string would be in all three. It is erased from the address bar immediately,
 *  so a shared screenshot or a copied URL does not carry it.
 */
const HANDOFF = (() => {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const token = p.get('token')
  const signin = p.get('s')
  // `pathname + search`, not `pathname`: erasing the token from the address bar was
  // taking any query parameters with it, so a link to a filtered view arrived signed
  // in but on the unfiltered default.
  if (token || signin) {
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
  return { token, signin }
})()
const HANDOFF_TOKEN = HANDOFF.token

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)

  // Every path that learns who you are also learns which zone the firm's
  // dates are in, so it is published here rather than at six call sites.
  // Synchronously, not in an effect: `Shell` builds a query key from
  // `todayISO()` on its very first render with `me` set, and an effect would
  // run after that render and ask the server for the wrong day once.
  const applyMe = useCallback((who: Me | null) => {
    setFirmZone(who?.timezone ?? '')
    setMe(who)
  }, [])

  const [ready, setReady] = useState(false)
  // AuthProvider is mounted inside BrowserRouter, so this is available here - which
  // is what lets a one-tap link land on the approval rather than on /today.
  const navigate = useNavigate()

  // On load there is no access token in memory - it never persists - but the
  // HttpOnly refresh cookie may still be valid, so try to resume silently.
  useEffect(() => {
    let alive = true
    ;(async () => {
      // Left alone for a day: end it before resuming it. Deliberately ahead of the
      // refresh below, because the cookie may well still be valid - the server
      // cannot see that nobody has touched this browser since yesterday, and an
      // open tab's own polling would have kept renewing it. A handoff link is
      // exempt: somebody has just tapped it, which is activity by definition.
      if (!HANDOFF_TOKEN && !HANDOFF.signin && idleTooLong()) {
        clearActivity()
        noteIdleSignOut()
        try { await api.post('/auth/logout') } catch { /* already gone */ }
        if (alive) { applyMe(null); setReady(true) }
        return
      }
      // Emergency access: `admin.py login-link` prints a URL carrying a 15-minute
      // token in the fragment, taken out of the URL by HANDOFF_TOKEN above. This runs
      // in every build, deliberately - it used to be a sessionStorage value read only
      // under `import.meta.env.DEV`, which Vite strips from a production bundle, so
      // the documented escape hatch for "mail is down" was inert on the deployed app.
      if (HANDOFF_TOKEN) {
        setAccessToken(HANDOFF_TOKEN)
        try {
          const who = await api.get<Me>('/auth/me')
          if (alive) { markActive(); applyMe(who); setReady(true) }
          return
        } catch { setAccessToken(null) }
      }

      // One-tap sign-in from a notification. Tried before /auth/refresh because the
      // refresh cookie is SameSite=Strict: arriving from the Telegram in-app browser
      // is a cross-site navigation, so the cookie may be withheld on exactly this
      // request even for somebody who has signed in on this phone before.
      //
      // The destination comes from the server's reply, not from the `to=` in the URL
      // we were handed - the fragment is editable by whoever holds the link, and the
      // server is the one that knows what this token was minted for.
      if (HANDOFF.signin) {
        try {
          const s = await api.post<{ access_token: string; next_path?: string }>(
            '/auth/exchange', { token: HANDOFF.signin })
          setAccessToken(s.access_token)
          const who = await api.get<Me>('/auth/me')
          if (alive) {
            markActive()
            applyMe(who)
            setReady(true)
            // `replace`, so Back does not return to a URL whose token is now spent.
            if (s.next_path) navigate(s.next_path, { replace: true })
          }
          return
        } catch {
          // A spent or expired link is not an error worth a red box: fall through to
          // the normal resume, and then to the sign-in screen, which is where they
          // need to end up anyway.
          setAccessToken(null)
        }
      }
      try {
        const s = await api.post<{ access_token: string }>('/auth/refresh')
        setAccessToken(s.access_token)
        const who = await api.get<Me>('/auth/me')
        // Opening the app is activity, and the stamp may be missing entirely on a
        // session that predates this rule - without one nothing would ever idle out.
        if (alive) { markActive(); applyMe(who) }
      } catch {
        if (alive) applyMe(null)
      } finally {
        if (alive) setReady(true)
      }
    })()
    return () => { alive = false }
    // Deliberately once, on mount: re-running this would re-attempt a spent token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setAuthLostHandler(() => { setAccessToken(null); applyMe(null) })
  }, [])

  const adopt = useCallback(async (s: { access_token: string }) => {
    setAccessToken(s.access_token)
    const who = await api.get<Me>('/auth/me')
    markActive()
    applyMe(who)
  }, [applyMe])

  const login = useCallback(async (email: string, password: string) => {
    await adopt(await api.post<{ access_token: string }>(
      '/auth/login', { email, password }))
  }, [adopt])

  // Deliberately returns nothing useful: the API answers the same way whether or
  // not the address can sign in, so the UI must not imply otherwise either.
  const requestCode = useCallback(async (email: string) => {
    await api.post('/auth/request-code', { email })
  }, [])

  const setPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await adopt(await api.post<{ access_token: string }>(
        '/auth/set-password', { email, code, new_password: newPassword }))
    }, [adopt])

  const signOut = useCallback(async () => {
    try { await api.post('/auth/logout') } catch { /* already gone */ }
    clearActivity()
    setAccessToken(null)
    applyMe(null)
  }, [applyMe])

  // Watching for a person, and for their absence.
  //
  // Only while signed in, so the sign-in screen neither stamps activity nor gets
  // swept. The stamp is written at most once a minute - `pointerdown` and `keydown`
  // fire hundreds of times a minute while somebody works, and this is a 24-hour
  // clock, not a stopwatch.
  const lastWrite = useRef(0)
  useEffect(() => {
    if (!me) return
    markActive()
    lastWrite.current = Date.now()

    const touched = () => {
      const now = Date.now()
      if (now - lastWrite.current < 60_000) return
      lastWrite.current = now
      markActive(now)
    }
    // `visibilitychange` counts only on the way back in: a tab going to the
    // background is somebody leaving, not somebody working.
    const returned = () => { if (!document.hidden) touched() }

    window.addEventListener('pointerdown', touched, { passive: true })
    window.addEventListener('keydown', touched, { passive: true })
    window.addEventListener('scroll', touched, { passive: true })
    document.addEventListener('visibilitychange', returned)

    // A minute's granularity on a 24-hour rule, which is plenty and costs nothing.
    // This is what ends an abandoned tab that is still polling happily away.
    const tick = window.setInterval(() => {
      if (!idleTooLong()) return
      noteIdleSignOut()
      void signOut()
    }, 60_000)

    return () => {
      window.removeEventListener('pointerdown', touched)
      window.removeEventListener('keydown', touched)
      window.removeEventListener('scroll', touched)
      document.removeEventListener('visibilitychange', returned)
      window.clearInterval(tick)
    }
  }, [me, signOut])

  const can = useCallback((action: string) => !!me?.permissions?.[action], [me])

  return (
    <Ctx.Provider value={{ me, ready, login, requestCode, setPassword, signOut, can }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

export { ApiError }
